import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Badge } from '@klaro7/ui';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { useUnreadMessages } from '@/features/chat/hooks/useUnreadMessages';
import { UserMenu } from './UserMenu';

const ROUTE_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  admin: 'Admin',
  users: 'Utenti',
  professionals: 'Professionisti',
  waitlist: "Lista d'Attesa",
  questionnaires: 'Questionari',
  services: 'Servizi Esterni',
  profile: 'Profilo',
  settings: 'Impostazioni',
};

interface Breadcrumb {
  label: string;
  path: string;
}

function useBreadcrumbs(): Breadcrumb[] {
  const location = useLocation();
  const segments = location.pathname.split('/').filter(Boolean);

  const crumbs: Breadcrumb[] = [];
  let currentPath = '';

  for (const segment of segments) {
    currentPath += `/${segment}`;
    const label = ROUTE_LABELS[segment];
    if (label) {
      crumbs.push({ label, path: currentPath });
    }
  }

  return crumbs;
}

interface HeaderProps {
  onMenuClick: () => void;
}

export function Header({ onMenuClick }: HeaderProps) {
  const { user } = useAuth();
  const breadcrumbs = useBreadcrumbs();
  const navigate = useNavigate();
  // Admin roles never handle their own chats — hide the button and skip the
  // unread fetch + dedicated socket entirely.
  const isAdminRole = user?.role === 'admin' || user?.role === 'super_admin';
  const unreadCount = useUnreadMessages(!isAdminRole);

  const roleLabel = user?.role?.replace(/_/g, ' ') || '';

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-brand-teal-100/60 bg-white/85 backdrop-blur-sm px-4 lg:px-6 shadow-[0_1px_3px_rgba(24,153,170,0.04)]">
      <div className="flex min-w-0 items-center gap-3">
        {/* Mobile hamburger */}
        <button
          onClick={onMenuClick}
          className="shrink-0 rounded-lg p-2 text-neutral-500 hover:bg-neutral-100 lg:hidden"
          aria-label="Apri menu"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
          </svg>
        </button>
        {/* Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1.5 text-sm">
        {breadcrumbs.map((crumb, index) => {
          const isLast = index === breadcrumbs.length - 1;

          return (
            <span
              key={crumb.path}
              className={
                isLast
                  ? 'flex min-w-0 items-center gap-1.5'
                  : 'hidden shrink-0 items-center gap-1.5 sm:flex'
              }
            >
              {index > 0 && (
                <svg className="hidden h-3.5 w-3.5 flex-shrink-0 text-neutral-400 sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              )}
              {isLast ? (
                <span className="truncate font-medium text-neutral-900" aria-current="page">{crumb.label}</span>
              ) : (
                <Link
                  to={crumb.path}
                  className="text-neutral-600 hover:text-neutral-700 transition-colors"
                >
                  {crumb.label}
                </Link>
              )}
            </span>
          );
        })}
      </nav>
      </div>

      {/* Right side */}
      <div className="flex shrink-0 items-center gap-3">
        {user && (
          <>
            <Badge variant="primary" className="text-xs capitalize bg-brand-teal/10 text-brand-teal-600">
              {roleLabel}
            </Badge>
            {!isAdminRole && (
            <button
              onClick={() => navigate('/my/chat')}
              className="relative rounded-lg p-2 text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
              aria-label={unreadCount > 0 ? `${unreadCount} messaggi non letti` : 'Messaggi'}
              title="Messaggi"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              {unreadCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-orange px-1 text-[10px] font-bold text-white">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
            )}
            <UserMenu />
          </>
        )}
      </div>
    </header>
  );
}
